import { all, takeEvery, select, call, put, take } from "redux-saga/effects";
import CryptoJS, { AES } from "crypto-js";

import { EAppActionTypes, ESubscriptionActionTypes } from "models";
import {
	LocalStorageKeyCustomToken,
	LocalStorageKey,
	CustomTokenPassword,
	rollbar,
} from "utils";
import {
	api,
	setUser,
	setAppLoading,
	getCommunitiesJoined,
	appSetData,
	getPosts,
	getEditProfile,
	history,
} from "store";
import { updateToken } from "../..";
import { TStore } from "models";
import {
	appChangeCommunity,
	getCommunity,
	getSubscriptionManagementActionSaga,
	getUserCurrentSubscriptionAction,
	SetWelcomeModal,
} from "store/actions";
import { getUnreadNotificationsCounterSagaAction } from "store/actions/notifications";

declare global {
	interface Window {
		OneSignal: any;
	}
}

function* appOneSignalResetIdHandle() {
	// yield put(storeLogout());
}

const getAuthenticatedUser: any = (state: TStore) => state.auth;

function* appInitHandle(data: any) {
	try {
		yield put(setAppLoading(true));
		const { userId, communityId, token } = data.payload;
		if (!userId || !token) {
			yield put(setAppLoading(false));
			return;
		}
		const customTokenValEncrypted = AES.encrypt(
			`${userId} ${communityId} ${token}`,
			CustomTokenPassword,
		);
		localStorage.setItem(
			LocalStorageKeyCustomToken,
			customTokenValEncrypted.toString(),
		);
		if (communityId) {
			yield put(
				appSetData({
					currentCommunityId: communityId,
					currentGroupId: "",
				}),
			);
		}
		yield put(setAppLoading(false));
	} catch (error) {
		yield put(setAppLoading(false));
		rollbar.error(error)
		console.error({ error });
	}
}

function* appSetUserHandle() {
	try {
		yield put(setAppLoading(true));
		const customToken = localStorage.getItem(LocalStorageKeyCustomToken);
		let user_id: number = 0;
		let community_id: number = 0;
		let token;
		if (customToken) {
			let decryptCustomToken = AES.decrypt(
				customToken,
				CustomTokenPassword,
			);
			let arrCustomToken = decryptCustomToken
				.toString(CryptoJS.enc.Utf8)
				.split(" ");
			user_id = parseInt(arrCustomToken[0]);
			community_id = parseInt(arrCustomToken[1]);
			token = arrCustomToken[2];
		}
		if (!token || !user_id) {
			//   yield put(storeLogout(payload));
			yield put(setAppLoading(false));
			return;
		}
		updateToken(token);
		yield put(getCommunitiesJoined());
		if (community_id) {
			const { data: communityData } = yield call(api.community.home, {
				community_id,
			});

			if (
				communityData.meta.privacy === "open" ||
				communityData.meta.is_approved
			) {
				yield put(
					getPosts({
						resetPosts: true,
						communityId: community_id,
					}),
				);
				yield put(
					getUnreadNotificationsCounterSagaAction({
						communityId: community_id,
					}),
				);
				const { data: userProfileData } = yield call(
					api.profile.getUserProfile,
					{
						user_id: user_id,
						community_id: community_id,
					},
				);

				yield put(
					getEditProfile({
						userId: user_id,
						communityId: community_id,
					}),
				);
				// TODO: add the editProfileData, profileAccountSettingsData and setUser in a single function
				const { data: editProfileData } = yield call(
					api.profile.getEditProfile,
					{
						user_id: user_id,
						community_id: community_id,
					},
				);
				const { data: profileAccountSettingsData } = yield call(
					api.profile.getProfileAccountSettings,
					{
						user_id: user_id,
						community_id: community_id,
					},
				);

				const userData = {
					user: {
						...editProfileData.user,
						...userProfileData.user,
						member_directory_opt_in:
							profileAccountSettingsData.user
								.member_directory_opt_in,
						community_id,
						joined_community_id: community_id,
					},
				};

				yield put(
					setUser({
						credentials: userData,
					}),
				);
				yield put(
					appSetData({
						currentCommunityId: community_id,
						currentGroupId: null,
					}),
				);
				yield put(
					getSubscriptionManagementActionSaga({
						communityId: community_id,
					}),
				);
				yield take(
					ESubscriptionActionTypes.COMPLETE_GET_SUBSCRIPTION_MANAGEMENT,
				);
			}
		} else {
			const { data: credentials } = yield call(
				api.profile.saveUserProfile,
				{
					user_id,
					user: {
						id: user_id,
					},
				},
			);

			yield put(
				setUser({
					credentials,
				}),
			);
			if (!credentials.user) yield history.push("/auth/sign-in");
			// if (!credentials.user.location) yield put(push("/welcome"));
			if (credentials.users) yield history.push("/communities");
		}
		yield put(setAppLoading(false));
	} catch (error: any) {
		rollbar.error(error)
		if (
			error.response &&
			error.response.data &&
			error.response.data &&
			error.response.data.meta
		) {
			if (
				Object.keys(error.response.data.meta).indexOf("is_approved") !==
				-1 &&
				!error.response.data.meta.is_approved
			) {
				yield history.push("/communities");
			}
		}
		yield put(setAppLoading(false));
	}
}

function* handleAppChangeCommunity({
	payload,
}: ReturnType<typeof appChangeCommunity>) {
	const {
		dontRedirect = false,
		onSuccessCallback,
		onErrorCallback,
	} = payload;
	try {
		yield put(setAppLoading(true));
		if (!payload.communityId) {
			throw new Error("Missing parameters.");
		}
		const { communityId } = payload;
		yield put(
			appSetData({
				currentCommunityId: communityId,
				currentGroupId: null,
			}),
		);
		yield put(
			getCommunity({
				communityId,
			}),
		);
		yield put(getUserCurrentSubscriptionAction({ communityId }));
		const authUser = yield select(getAuthenticatedUser);
		const token = localStorage.getItem(LocalStorageKey);
		const customTokenValEncrypted = AES.encrypt(
			`${authUser.credentials.user.id} ${communityId} ${token}`,
			CustomTokenPassword,
		);
		localStorage.setItem(
			LocalStorageKeyCustomToken,
			customTokenValEncrypted.toString(),
		);
		if (!dontRedirect) {
			yield history.push({ pathname: "/dashboard" });
			yield put(SetWelcomeModal(true))
		}
		onSuccessCallback?.();
		// For the rest of entities I will add an useEffect that will fetch the community specific data.
		yield put(setAppLoading(false));
	} catch (error) {
		rollbar.error(error)
		if (
			error.response &&
			error.response.data &&
			error.response.data &&
			error.response.data.meta
		) {
			if (
				Object.keys(error.response.data.meta).indexOf("is_approved") !==
				-1 &&
				!error.response.data.meta.is_approved
			) {
				if (!dontRedirect) {
					yield history.push("/communities");
				}
			}
		}
		console.error({ error });
		yield put(setAppLoading(false));
		onErrorCallback?.();
	}
}

export default function* rootSaga() {
	yield all([
		takeEvery(
			EAppActionTypes.appOneSignalResetId,
			appOneSignalResetIdHandle,
		),
		takeEvery(EAppActionTypes.appInit, appInitHandle),
		takeEvery(EAppActionTypes.appChangeCommunity, handleAppChangeCommunity),
		takeEvery(EAppActionTypes.appSetUser, appSetUserHandle),
	]);
}
